import {
    type BankingAdoptionTaskStatus,
    type GuidanceSessionType,
    type MoneyMapStatus,
    type StripeSubscriptionStatus,
} from '@fruitful/types';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect } from 'react';

import type { ContainerComponent } from '../../common/types/Common';
import { requestBuilder } from '../../http/RequestBuilder';
import {
    adminUserAtom,
    allMembershipsAtom,
    isLoadingMembershipsAtom,
} from '../entityAtoms';
import { type MembershipSummary, type OnboardingStatus } from '../entityTypes';
import { transposeMembershipSummaryDto } from './dto/MembershipDto';

export interface MembershipSummaryGuidanceSession {
    startTime: string;
    type: GuidanceSessionType;
    status: string;
}

// Maps to GetAllAdminMembershipModel on backend
export interface MembershipSummaryDto {
    id: string;
    guideId: string | null;
    createdAt: string;
    updatedAt: string | null;
    stripeProductName: string | null;
    stripeBillingFrequency: string | null;
    stripeStatus: StripeSubscriptionStatus | null;
    hasPlusFeatures: boolean;
    firstNonZeroPaymentAt: string | null;
    users: Array<{
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
        phoneNumber: string;
        isSubscriptionActive: boolean;
        onboardingStatus: OnboardingStatus;
        cancellationDate: string | null;
        cancellationReason: string | null;
        sendbirdUserId: string | null;
        orionUser: {
            id: string;
        } | null;
        bankingAdoption: {
            id: string;
            directDepositStatus: BankingAdoptionTaskStatus;
        } | null;
        apexAccountParties:
            | [
                  {
                      id: string;
                  },
              ]
            | null;
        userAddress: {
            state: string | null;
        } | null;
    }>;
    guidanceSession: MembershipSummaryGuidanceSession[];
    moneyMaps: Array<{
        status: MoneyMapStatus;
    }>;
}

export const AllMembershipsQueryKey = `getAllMemberships`;

const useGetAllMemberships = requestBuilder.query<MembershipSummary[]>({
    endpoint: '/api/admin/memberships',
    queryKey: AllMembershipsQueryKey,
});

export const MembershipEntityProvider: ContainerComponent = ({ children }) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canFetchMemberships = adminUser.authorization.memberships.canViewAll;
    const setAllMemberships = useSetAtom(allMembershipsAtom);
    const setIsLoadingMemberships = useSetAtom(isLoadingMembershipsAtom);

    const { data: memberships, isLoading: isLoadingMemberships } =
        useGetAllMemberships(null, {
            enabled: canFetchMemberships,
            select: useCallback((data: MembershipSummaryDto[]) => {
                return data.map(transposeMembershipSummaryDto);
            }, []),
            refetchInterval: 600000, // 10 minutes
        });

    useEffect(() => {
        setIsLoadingMemberships(isLoadingMemberships);
    }, [isLoadingMemberships]);

    useEffect(() => {
        if (memberships !== undefined) {
            setAllMemberships(memberships);
        }
    }, [memberships]);

    return <>{children}</>;
};
